import { useState, useEffect, useRef } from 'react';

/**
 * Hook personnalisé pour détecter quand un élément devient visible dans le viewport
 * Utilise l'API Intersection Observer pour une performance optimale
 * 
 * @param {Object} options - Options de configuration
 * @param {string} options.rootMargin - Marge autour de la racine (ex: '200px 0px')
 * @param {number} options.threshold - Seuil de visibilité (0 à 1)
 * @param {boolean} options.triggerOnce - Si true, déclenche une seule fois
 * @returns {Array} - [ref, inView] où ref est la référence à attacher à l'élément et inView est un booléen indiquant si l'élément est visible
 */
export function useInView({
  rootMargin = '0px',
  threshold = 0,
  triggerOnce = false
} = {}) {
  const supportsIO = typeof window !== 'undefined' && 'IntersectionObserver' in window;
  const [inView, setInView] = useState(() => (supportsIO ? false : true));
  const ref = useRef(null);
  const enteredView = useRef(false);

  useEffect(() => {
    // Si l'élément a déjà été vu et triggerOnce est true, ne pas recréer l'observer
    if (triggerOnce && enteredView.current) {
      return;
    }

    const element = ref.current;
    if (!element) return;

    // Vérifier si IntersectionObserver est disponible
    if (!supportsIO) {
      // Fallback pour les navigateurs qui ne supportent pas IntersectionObserver
      // L'état initial a déjà été défini à true pour ce cas
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        setInView(isVisible);

        if (isVisible && triggerOnce) {
          enteredView.current = true;
          observer.disconnect();
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [rootMargin, threshold, triggerOnce, supportsIO]);

  return [ref, inView];
}

/**
 * Fonction utilitaire pour charger les images paresseusement
 * Initialise un IntersectionObserver global pour toutes les images avec la classe 'lazyload'
 */
export function initLazyLoading() {
  // Fonction pour initialiser l'observation des images
  function setupObserver() {
    if (!('IntersectionObserver' in window)) {
      // Fallback pour les navigateurs qui ne supportent pas IntersectionObserver
      
      const lazyImages = document.querySelectorAll('img.lazyload');
      lazyImages.forEach(img => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.classList.remove('lazyload');
        }
      });
      return;
    }

    const lazyImageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const lazyImage = entry.target;
          if (lazyImage.dataset.src) {
            // Créer une nouvelle image pour précharger
            const img = new Image();
            
            // Configurer les gestionnaires d'événements avant de définir src
            img.onload = function() {
              // Une fois l'image chargée, mettre à jour l'élément visible
              lazyImage.src = lazyImage.dataset.src;
              lazyImage.classList.remove('lazyload');
            };
            
            img.onerror = function() {
              lazyImage.classList.remove('lazyload');
            };
            
            // Déclencher le chargement
            img.src = lazyImage.dataset.src;
            
            // Arrêter d'observer cette image
            lazyImageObserver.unobserve(lazyImage);
          }
        }
      });
    }, {
      rootMargin: '200px 0px', // Charge les images 200px avant qu'elles n'entrent dans le viewport
      threshold: 0.1 // Déclenche quand au moins 10% de l'image est visible
    });

    // Observer toutes les images avec la classe lazyload
    const lazyImages = document.querySelectorAll('img.lazyload');
    lazyImages.forEach(image => {
      lazyImageObserver.observe(image);
    });
  }

  // Initialiser immédiatement
  setupObserver();
  
  // Réinitialiser l'observateur lors des changements de DOM (pour les nouvelles images)
  document.addEventListener('DOMContentLoaded', setupObserver);
  
  // Vérifier périodiquement s'il y a de nouvelles images à observer
  const checkInterval = setInterval(() => {
    const lazyImages = document.querySelectorAll('img.lazyload');
    if (lazyImages.length > 0) {
      setupObserver();
    }
  }, 2000); // Vérifier toutes les 2 secondes
  
  // Nettoyer l'intervalle lors du démontage
  return () => clearInterval(checkInterval);
}

export default useInView;
